/* ============================================================================
   ICONOS · un solo set para todo el sistema
   ----------------------------------------------------------------------------
   Hoy conviven CINCO sets (boxicons en 40 archivos, @ant-design/icons en 29,
   mdi, Font Awesome, react-icons). Este es el único que deben usar los
   componentes nuevos y las pantallas migradas.

   Reglas de la referencia, literales: trazo 1.7-1.8, extremos redondeados,
   tamaños 16/18/20/24 y color SIEMPRE `currentColor`, para que herede del
   texto y funcione en los tres temas sin una sola regla de color.

   Los trazados son los de `referencia-componentes.html`: un concepto, un
   icono, en todo el sistema.
   ========================================================================== */

const base = (size, grosor) => ({
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: grosor,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    focusable: "false",
});

const crear = (nombre, trazos, grosorPorDefecto = 1.8) => {
    const Icono = ({ size = 18, grosor = grosorPorDefecto, ...resto }) => (
        <svg {...base(size, grosor)} {...resto}>
            {trazos}
        </svg>
    );
    Icono.displayName = `Icono${nombre}`;
    return Icono;
};

/* -- acciones ------------------------------------------------------------- */
export const IconoEditar = crear(
    "Editar",
    <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </>,
    1.7
);
export const IconoVer = crear(
    "Ver",
    <>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
    </>,
    1.7
);
export const IconoEliminar = crear(
    "Eliminar",
    <>
        <path d="M4 7h16" />
        <path d="M9 7V5h6v2" />
        <path d="M6 7l1 13h10l1-13" />
    </>,
    1.7
);
// Subir: la misma bandeja que descargar, con la flecha al reves.
export const IconoSubir = crear(
    "Subir",
    <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="M7 9l5-5 5 5" />
        <path d="M12 4v12" />
    </>,
    1.8
);
export const IconoDescargar = crear(
    "Descargar",
    <>
        <path d="M12 3v12" />
        <path d="M7 10l5 5 5-5" />
        <path d="M4 21h16" />
    </>,
    1.7
);
export const IconoHistorial = crear(
    "Historial",
    <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5" />
        <path d="M12 8v4l3 2" />
    </>,
    1.7
);
export const IconoFiltrar = crear(
    "Filtrar",
    <>
        <path d="M3 5h18" />
        <path d="M6 12h12" />
        <path d="M10 19h4" />
    </>,
    1.7
);
export const IconoBuscar = crear(
    "Buscar",
    <>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
    </>,
    1.7
);
export const IconoAuditar = crear(
    "Auditar",
    <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
    </>,
    1.7
);
export const IconoMas = crear(
    "Mas",
    <>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
    </>,
    2
);
// Los tres puntos van RELLENOS: dibujados solo con trazo quedaban como tres
// anillos de 1.6px de radio, practicamente invisibles a 16px (reportado en
// pruebas: "los tres puntos no se ven").
export const IconoTresPuntos = ({ size = 18, ...resto }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
        aria-hidden="true"
        focusable="false"
        {...resto}
    >
        <circle cx="12" cy="5" r="1.9" />
        <circle cx="12" cy="12" r="1.9" />
        <circle cx="12" cy="19" r="1.9" />
    </svg>
);
export const IconoCerrar = crear(
    "Cerrar",
    <>
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
    </>,
    2
);
export const IconoCheck = crear("Check", <path d="M20 6L9 17l-5-5" />, 2.4);
// Revertir: flecha antihoraria SIN reloj -- el reloj es "historial", que es
// otro concepto. Un concepto, un icono.
export const IconoRevertir = crear(
    "Revertir",
    <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5" />
    </>,
    1.7
);

/* -- estado y avisos ------------------------------------------------------ */
export const IconoInfo = crear(
    "Info",
    <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
    </>,
    1.9
);
export const IconoError = crear(
    "Error",
    <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5" />
        <path d="M12 17h.01" />
    </>,
    1.9
);
export const IconoAlerta = crear(
    "Alerta",
    <>
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </>,
    1.9
);
export const IconoEscudo = crear(
    "Escudo",
    <>
        <path d="M12 3l7 3v5c0 4.4-2.9 8.2-7 10-4.1-1.8-7-5.6-7-10V6l7-3z" />
        <path d="M9.5 12l1.8 1.8L15 10" />
    </>,
    1.8
);
export const IconoCargando = crear("Cargando", <path d="M12 3a9 9 0 1 0 9 9" />, 2.4);
// Prohibido / detener: es lo que se usa para inactivar o cancelar algo sin
// borrarlo. NO es la papelera: no destruye, deja de estar disponible.
export const IconoProhibido = crear(
    "Prohibido",
    <>
        <circle cx="12" cy="12" r="9" />
        <path d="M5.6 5.6l12.8 12.8" />
    </>,
    1.8
);

/* -- documentos ----------------------------------------------------------- */
// Documento nuevo: la hoja con la esquina doblada, mas un "+".
export const IconoDocumentoMas = crear(
    "DocumentoMas",
    <>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
        <path d="M14 3v5h5" />
        <path d="M12 11v6" />
        <path d="M9 14h6" />
    </>,
    1.8
);
// Documento cerrado/emitido: la misma hoja con el visto.
export const IconoDocumentoHecho = crear(
    "DocumentoHecho",
    <>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
        <path d="M14 3v5h5" />
        <path d="M9 15l2 2 4-4" />
    </>,
    1.8
);
/* -- navegación ----------------------------------------------------------- */
export const IconoChevronAbajo = crear("ChevronAbajo", <path d="M6 9l6 6 6-6" />, 1.8);
export const IconoChevronDerecha = crear("ChevronDerecha", <path d="M9 6l6 6-6 6" />, 1.8);
export const IconoChevronIzquierda = crear("ChevronIzquierda", <path d="M15 18l-6-6 6-6" />, 1.8);
export const IconoCalendario = crear(
    "Calendario",
    <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18" />
        <path d="M8 3v4" />
        <path d="M16 3v4" />
    </>,
    1.8
);

/* TARIFARIOS-UI: el ID de Contífico deja de ser una columna ilegible y pasa a
   vivir en el modal, donde se puede COPIAR. Un botón de copiar necesita el
   icono universal de copiar; el sistema no lo tenía. */
export const IconoCopiar = crear(
    "Copiar",
    <>
        <rect x="9" y="9" width="12" height="12" rx="2" />
        <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </>,
    1.8
);

/* Flecha de transición "de esto a esto": el cambio de precio ($20 -> $22). */
export const IconoFlecha = crear(
    "Flecha",
    <>
        <path d="M5 12h14" />
        <path d="M13 6l6 6-6 6" />
    </>,
    2
);

export const ICONOS = {
    editar: IconoEditar,
    ver: IconoVer,
    eliminar: IconoEliminar,
    descargar: IconoDescargar,
    subir: IconoSubir,
    historial: IconoHistorial,
    filtrar: IconoFiltrar,
    buscar: IconoBuscar,
    auditar: IconoAuditar,
    mas: IconoMas,
    tresPuntos: IconoTresPuntos,
    cerrar: IconoCerrar,
    check: IconoCheck,
    prohibido: IconoProhibido,
    revertir: IconoRevertir,
    documentoMas: IconoDocumentoMas,
    documentoHecho: IconoDocumentoHecho,
    info: IconoInfo,
    error: IconoError,
    alerta: IconoAlerta,
    escudo: IconoEscudo,
    cargando: IconoCargando,
    chevronAbajo: IconoChevronAbajo,
    chevronDerecha: IconoChevronDerecha,
    chevronIzquierda: IconoChevronIzquierda,
    calendario: IconoCalendario,
    copiar: IconoCopiar,
    flecha: IconoFlecha,
};
